import { useState } from 'react';
import { Star, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ReviewsList = ({ reviews, stats, isDark = false }) => {
  const [visibleCount, setVisibleCount] = useState(3);
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : isDark ? 'text-gray-600' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 3);
  };

  const initialsOf = (name) => (name || 'A').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const goldStars = (n) => '★★★★★'.slice(0, Math.max(0, Math.min(5, Math.round(n)))).padEnd(5, '☆');

  // ============================ DARK (Command Deck) ============================
  if (isDark) {
    const cardStyle = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' };
    const avatarGrads = ['linear-gradient(135deg,#572B7C,#28C0D0)', 'linear-gradient(135deg,#28C0D0,#67C6B4)', 'linear-gradient(135deg,#78287C,#572B7C)'];
    if (!reviews || reviews.length === 0) {
      return (
        <div style={cardStyle}>
          <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 10 }}>Reviews &amp; ratings</div>
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,.45)' }}>
            <Star size={36} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
            <p style={{ fontSize: 13 }}>No reviews yet</p>
            <p style={{ fontSize: 12, marginTop: 2, color: 'rgba(255,255,255,.35)' }}>Reviews appear here after completed jobs.</p>
          </div>
        </div>
      );
    }
    const visibleReviews = reviews.slice(0, visibleCount);
    const hasMore = visibleCount < reviews.length;
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Reviews &amp; ratings</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#FCD34D', fontSize: 13 }}>{goldStars(stats.average_rating)}</span>
            <span className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{stats.average_rating}</span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>· {stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {visibleReviews.map((review, i) => {
            const isClient = review.reviewer_type === 'client';
            return (
              <div key={review.id || i} style={{ paddingBottom: i < visibleReviews.length - 1 ? 14 : 0, borderBottom: i < visibleReviews.length - 1 ? '1px solid rgba(255,255,255,.07)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 999, background: avatarGrads[i % avatarGrads.length], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flex: '0 0 auto' }} className="cd-display">
                    <span style={{ fontWeight: 700, fontSize: 11 }}>{initialsOf(review.reviewer_name)}</span>
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {review.reviewer_name || 'Anonymous'}
                      <span style={{ fontSize: 10, fontWeight: 600, color: isClient ? '#28C0D0' : '#C98AD4', background: isClient ? 'rgba(40,192,208,.16)' : 'rgba(120,40,124,.22)', padding: '2px 7px', borderRadius: 999 }}>{isClient ? 'Client' : 'Crew'}</span>
                    </div>
                  </div>
                  <span style={{ color: '#FCD34D', fontSize: 12 }}>{goldStars(review.rating || 0)}</span>
                </div>
                {(review.review_text || review.comment) && (
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,.65)', lineHeight: 1.55 }}>{review.review_text || review.comment}</div>
                )}
                {(review.job_title || review.created_at) && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 6 }}>
                    {review.job_title ? `Job: ${review.job_title}` : ''}{review.job_title && review.created_at ? ' · ' : ''}{review.created_at ? formatDate(review.created_at) : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button type="button" onClick={handleLoadMore} className="cd-btn" style={{ width: '100%', marginTop: 16, padding: 11 }}>Load more reviews</button>
        )}
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <Card className={`${isDark ? 'bg-white/[0.045] border-white/10' : 'bg-white border-gray-200'} shadow-xl`} style={isDark ? { borderRadius: 18 } : undefined}>
        <CardHeader className="border-b border-gray-200/10">
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>
            Reviews
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Star className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No reviews yet</p>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Reviews will appear here after completed jobs
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;

  return (
    <Card className={`${isDark ? 'bg-white/[0.045] border-white/10' : 'bg-white border-gray-200'} shadow-xl`} style={isDark ? { borderRadius: 18 } : undefined}>
      <CardHeader className="border-b border-gray-200/10">
        <div className="flex items-center justify-between">
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>
            Reviews & Ratings
          </CardTitle>
          <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-400/30">
            {stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Overall Rating Summary */}
        <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500">
                {stats.average_rating}
              </div>
              <div className="flex justify-center mt-1">
                {renderStars(Math.round(stats.average_rating))}
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'}
              </p>
            </div>
            
            {/* Rating Breakdown */}
            <div className="flex-1">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = stats.rating_breakdown[star] || 0;
                const percentage = stats.total_reviews > 0 
                  ? (count / stats.total_reviews) * 100 
                  : 0;
                
                return (
                  <div key={star} className="flex items-center gap-2 mb-1">
                    <span className={`text-xs w-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {star}★
                    </span>
                    <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-full rounded-full bg-yellow-400"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className={`text-xs w-8 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Individual Reviews - Now Paginated */}
        <div className="space-y-4">
          {visibleReviews.map((review) => (
            <div
              key={review.id}
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-white/5 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {review.reviewer_name || 'Anonymous'}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {review.reviewer_type === 'client' ? 'Client' : 'Crew'}
                    </Badge>
                  </div>
                  {renderStars(review.rating || 0)}
                </div>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatDate(review.created_at)}
                </span>
              </div>
              
              {(review.review_text || review.comment) && (
                <p className={`text-sm mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {review.review_text || review.comment}
                </p>
              )}
              
              {review.job_title && (
                <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Job: {review.job_title}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-6 text-center">
            <Button
              onClick={handleLoadMore}
              variant="outline"
              className={`${isDark ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
            >
              Load More Reviews
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewsList;
